import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';

// --- COMPONENTS ---

const InputField = ({ label, value, onChange, type = "text", required = false, options = null, rows = 4, placeholder = "" }) => (
  <div className="space-y-1.5 w-full">
    <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-medium">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {options ? (
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          required={required}
          className="w-full bg-transparent border-b border-gray-200 py-2.5 text-sm text-gray-900 focus:border-black focus:outline-none transition-colors appearance-none"
        >
          <option value="">Select a subject...</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <span className="absolute right-0 top-3 pointer-events-none text-gray-400 text-[10px]">▼</span>
      </div>
    ) : type === "textarea" ? (
      <textarea
        value={value}
        onChange={onChange}
        required={required}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-gray-200 py-2.5 text-sm text-gray-900 placeholder:text-gray-300 placeholder:text-[11px] placeholder:font-light placeholder:italic focus:border-black focus:outline-none transition-colors resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-gray-200 py-2.5 text-sm text-gray-900 placeholder:text-gray-300 placeholder:text-[11px] placeholder:font-light placeholder:italic focus:border-black focus:outline-none transition-colors"
      />
    )}
  </div>
);

export default function SupportPage() {
  const [user, setUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const subjects = [
    'General Inquiry',
    'Order Status',
    'Returns & Exchanges',
    'Custom Tailoring',
    'Other'
  ];

  // Load user data if available
  useEffect(() => {
    const userData = localStorage.getItem('emba_user');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        const u = parsed.user || parsed;
        setUser(u);
      } catch (e) {
        console.error("Failed to parse user", e);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1200));

    // Show success alert using SweetAlert2
    Swal.fire({
      icon: 'success',
      title: 'Request Submitted',
      text: 'Thank you for reaching out! Our team will get back to you shortly.',
      confirmButtonColor: '#000000',
      customClass: {
        popup: 'rounded-none shadow-xl border border-gray-100',
        title: 'font-serif text-2xl text-gray-900 tracking-wide',
        confirmButton: 'text-[10px] tracking-widest uppercase rounded-none px-8 py-3'
      }
    });

    setForm({
      name: '',
      email: '',
      subject: '',
      message: ''
    });

    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-gray-900 flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="text-xs tracking-[0.2em] font-bold uppercase hover:opacity-70 transition-opacity">
            EMBA by Emefa
          </Link>
          <div className="flex items-center gap-6">
            <nav className="hidden md:flex gap-8 text-[10px] tracking-widest uppercase font-medium text-gray-500">
              <Link to="/" className="hover:text-black transition-colors">Home</Link>
              {/* <Link to="/store" className="hover:text-black transition-colors">Shop</Link> */}
              {localStorage.getItem('emba_access') ? (
                <Link to="/profile" className="hover:text-black transition-colors">Account</Link>
              ) : (
                <Link to="/login" className="hover:text-black transition-colors">Sign In</Link>
              )}
            </nav>
            <Link to="/bookings" className="text-[10px] uppercase tracking-widest bg-black text-white px-4 py-2 hover:bg-gray-800 transition-colors hidden md:block">
              Book Appointment
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-32 pb-20 px-4 md:px-6 flex items-center justify-center">
        <div className="w-full max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          
          {/* Left Side: Info */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="hidden md:flex flex-col h-full bg-[#1b0d0b] text-white p-12 justify-center"
          >
            <h1 className="text-4xl lg:text-5xl font-serif mb-6 leading-tight">We're here<br/>to help.</h1>
            <p className="text-white/60 font-light mb-12 tracking-wide leading-relaxed text-sm">
              Whether you have a question about our bespoke tailoring process, an existing order, or styling advice, our dedicated support team is ready to assist you.
            </p>
            
            <div className="space-y-8 text-xs tracking-widest text-white/80">
              <div>
                <h4 className="text-[10px] font-bold text-white mb-2 uppercase opacity-50">Email Us</h4>
                <p>emefabansa@gmail.com</p>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-white mb-2 uppercase opacity-50">Call Us</h4>
                <p>+233 542 661 704</p>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-white mb-2 uppercase opacity-50">Working Hours</h4>
                <p>Mon – Sat, 9:00 – 18:00</p>
              </div>
            </div>
          </motion.div>

          {/* Right Side: Form */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 h-full"
          >
            <div className="md:hidden mb-8">
              <h1 className="text-3xl font-serif mb-2">Get in touch</h1>
              <p className="text-xs text-gray-500">Fill out the form below and we'll get back to you.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <InputField 
                label="Full Name" 
                value={form.name} 
                onChange={(e) => setForm({...form, name: e.target.value})} 
                placeholder="e.g. John Doe"
                required 
              />
              <InputField 
                label="Email Address" 
                type="email" 
                value={form.email} 
                onChange={(e) => setForm({...form, email: e.target.value})} 
                placeholder="e.g. hello@example.com"
                required 
              />
              <InputField 
                label="Subject" 
                options={subjects}
                value={form.subject} 
                onChange={(e) => setForm({...form, subject: e.target.value})} 
                required 
              />
              <InputField 
                label="Message" 
                type="textarea"
                value={form.message} 
                onChange={(e) => setForm({...form, message: e.target.value})} 
                placeholder="How can we help you today?"
                required 
              />

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-black text-white px-8 py-4 text-[10px] tracking-[0.2em] uppercase hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  {submitting && (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </motion.div>

        </div>
      </main>
    </div>
  );
}
